import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import TabbedDetailCard from '#/modules/commonComponents/layout/TabbedDetailCard';
import CustomerOrderCollectionSection
  from '#/modules/crmMgmt/customerOrder/components/CustomerOrderCollectionSection';
import CustomerOrderLinesSection
  from '#/modules/crmMgmt/customerOrder/components/CustomerOrderLinesSection';
import CustomerOrderSalesOrdersSection
  from '#/modules/crmMgmt/customerOrder/components/CustomerOrderSalesOrdersSection';
import CustomerOrderWorkOrdersSection
  from '#/modules/crmMgmt/customerOrder/components/CustomerOrderWorkOrdersSection';
import {
  canCreateManufacturingWorkOrdersFromCustomerOrder,
  canCreateSalesOrderFromCustomerOrder,
} from '#/modules/crmMgmt/customerOrder/helper/customerOrderStatus';

function buildCreateSalesOrderPath(customerOrderId) {
  if (!customerOrderId) {
    return undefined;
  }
  return `${ROUTES.salesOrderCreate}?customerOrderId=${encodeURIComponent(customerOrderId)}`;
}

function renderCreateSalesOrderAction(customerOrderId, enabled) {
  if (enabled && customerOrderId) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to={buildCreateSalesOrderPath(customerOrderId)}>建立排程</Link>
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" variant="outline" disabled>
      建立排程
    </Button>
  );
}

function renderCreateManufacturingWorkOrdersAction(customerOrderId, enabled) {
  if (enabled && customerOrderId) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to={ROUTES.customerOrderManufacturingMaterials(customerOrderId)}>
          建立多階層工單
        </Link>
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" variant="outline" disabled>
      建立多階層工單
    </Button>
  );
}

/** Detail tabs：訂單明細／銷貨單／工單／收款（DRAFT 尚無收款計畫，不顯示）。 */
const CustomerOrderDetailTabs = ({ detail }) => {
  const status = detail?.status;
  const customerOrderId = detail?.id;
  const canCreateSalesOrder = canCreateSalesOrderFromCustomerOrder(status);
  const showCollection = Boolean(customerOrderId) && status !== 'DRAFT';
  const canCreateManufacturingWorkOrders = (
    canCreateManufacturingWorkOrdersFromCustomerOrder(status)
  );

  return (
    <TabbedDetailCard defaultValue="lines" className="2xl:col-span-2">
      <TabbedDetailCard.List>
        <TabbedDetailCard.Trigger value="lines">訂單明細</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="salesOrders">銷貨單</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="workOrders">工單</TabbedDetailCard.Trigger>
        {showCollection ? (
          <TabbedDetailCard.Trigger value="collection">收款</TabbedDetailCard.Trigger>
        ) : null}
      </TabbedDetailCard.List>

      <TabbedDetailCard.Pane value="lines" title="訂單明細">
        <CustomerOrderLinesSection
          lines={detail?.lines ?? []}
          manualLines={detail?.manualLines ?? []}
          customerOrderId={customerOrderId}
          status={status}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="salesOrders"
        title="銷貨單"
        actions={renderCreateSalesOrderAction(customerOrderId, canCreateSalesOrder)}
      >
        <CustomerOrderSalesOrdersSection
          salesOrders={detail?.salesOrders ?? []}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="workOrders"
        title="工單"
        actions={renderCreateManufacturingWorkOrdersAction(
          customerOrderId,
          canCreateManufacturingWorkOrders,
        )}
      >
        <CustomerOrderWorkOrdersSection
          workOrderMtrs={detail?.workOrderMtrs ?? []}
          customerOrderId={customerOrderId}
        />
      </TabbedDetailCard.Pane>

      {showCollection ? (
        <TabbedDetailCard.Pane value="collection" title="收款">
          <CustomerOrderCollectionSection detail={detail} />
        </TabbedDetailCard.Pane>
      ) : null}
    </TabbedDetailCard>
  );
};

CustomerOrderDetailTabs.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({ updatedAt: PropTypes.string }),
    lines: PropTypes.arrayOf(PropTypes.object),
    manualLines: PropTypes.arrayOf(PropTypes.object),
    salesOrders: PropTypes.arrayOf(PropTypes.object),
    workOrderMtrs: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default CustomerOrderDetailTabs;
